import { CSSProperties, Fragment, JSX } from "react";
import { cn } from "@/lib/utils";
import {
  CHART_PLOT_HEIGHT,
  DATA_TABLE_ROWS_PER_PAGE,
  DUAL_METRIC_TICK_SEGMENTS,
  FEATURE_COMPARISON_ROWS_PER_PAGE,
  RING_KPI_RING_SIZE,
  RING_KPI_TILE_MIN_WIDTH,
} from "@/config/layout";
import {
  CORRELATION_DEFAULT_FEATURE_COUNT,
  CORRELATION_FRAME_PAD,
  correlationFrameSize,
} from "./modules/module-correlation-heatmap";
import { QUADRANT_ORDER, quadrantOf, type QuadrantKey } from "./modules/quadrant-meta";

const Bar = ({
  className,
  style,
}: {
  className?: string;
  style?: CSSProperties;
}) => (
  <div
    className={cn("bg-neutral-200 rounded-md animate-pulse", className)}
    style={style}
  />
);

const headingSkeleton = () => <Bar className="h-5 w-2/5" />;

const chartFrameSkeleton = (barCount = 6) => (
  <div className="flex w-full">
    <div className="flex flex-col gap-6 pr-2 py-2 shrink-0">
      {Array.from({ length: 5 }).map((_, i) => (
        <Bar key={i} className="h-2 w-6" />
      ))}
    </div>

    <div className="flex-1 border-l border-neutral-200 flex flex-col">
      <div className="flex-1 flex flex-row items-end gap-2 px-2 py-2 min-h-[200px]">
        {Array.from({ length: barCount }).map((_, i) => {
          const heights = [60, 40, 85, 55, 72, 30, 68, 48];
          return (
            <Bar
              key={i}
              className="flex-1"
              style={{ height: `${heights[i % heights.length]}%` }}
            />
          );
        })}
      </div>
      <div className="flex flex-row justify-between px-2 pb-1 gap-2">
        {Array.from({ length: barCount }).map((_, i) => (
          <Bar key={i} className="h-2 w-8" />
        ))}
      </div>
    </div>
  </div>
);

const lineChartSkeleton = () => (
  <div className="flex w-full">
    <div className="flex flex-col gap-5 pr-2 py-2 shrink-0">
      {Array.from({ length: 5 }).map((_, i) => (
        <Bar key={i} className="h-2 w-6" />
      ))}
    </div>

    <div className="flex-1 border-l border-neutral-200 flex flex-col">
      <div className="relative flex-1 min-h-[180px]">
        <Bar className="absolute inset-x-2 top-1/2 -translate-y-1/2 h-[2px] rounded-none opacity-70" />
        <div className="absolute inset-0 flex flex-col justify-between py-2 px-2 opacity-30">
          {Array.from({ length: 4 }).map((_, i) => (
            <Bar key={i} className="h-px rounded-none" />
          ))}
        </div>
      </div>
      <div className="flex flex-row justify-between px-2 pb-1 gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <Bar key={i} className="h-2 w-8" />
        ))}
      </div>
    </div>
  </div>
);

const pieChartSkeleton = () => (
  <div className="flex w-full items-center justify-center py-4">
    <div className="relative w-[180px] h-[180px]">
      <Bar className="absolute inset-0 rounded-full" />
      <div className="absolute inset-[22%] rounded-full bg-white" />
    </div>
  </div>
);

const imageSkeleton = () => (
  <div className="w-full flex flex-col gap-2">
    <Bar
      className="w-full rounded-lg"
      style={{ aspectRatio: "16 / 9" }}
    />
    <Bar className="h-2.5 w-3/5" />
  </div>
);

const progressSkeleton = (rows = 3) => (
  <div className="w-full flex flex-col gap-4">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="w-full">
        <div className="flex justify-between mb-1">
          <Bar className="h-3 w-1/3" />
          <Bar className="h-3 w-10" />
        </div>
        <div className="w-full bg-neutral-100 rounded-full h-2">
          <Bar
            className="h-full rounded-full"
            style={{ width: `${40 + ((i * 23) % 50)}%` }}
          />
        </div>
      </div>
    ))}
  </div>
);

const quoteSkeleton = () => (
  <div className="flex flex-col gap-2">
    <Bar className="h-5 w-5 rounded-sm opacity-60" />
    <Bar className="h-4 w-full" />
    <Bar className="h-4 w-11/12" />
    <Bar className="h-4 w-2/3" />
    <div className="flex flex-row justify-between items-start mt-1">
      <div className="flex flex-col gap-1">
        <Bar className="h-3 w-24" />
        <Bar className="h-3 w-20" />
      </div>
      <Bar className="h-5 w-5 rounded-sm opacity-60" />
    </div>
  </div>
);

const introSkeleton = () => (
  <div className="flex flex-col justify-center gap-4 h-full">
    <div className="flex flex-col gap-3 bg-primary/80 p-4 rounded-sm">
      <Bar className="h-3 w-32 bg-white/30" />
      <Bar className="h-6 w-3/4 bg-white/30" />
      <Bar className="h-3 w-full bg-white/30" />
      <Bar className="h-3 w-5/6 bg-white/30" />
      <Bar className="h-3 w-24 bg-white/30 mt-1" />
    </div>
    <div className="grid grid-cols-7 bg-muted-foreground/10 rounded-md overflow-hidden">
      <Bar className="col-span-3 w-full h-24 rounded-none" />
      <div className="inline-flex flex-col gap-2 col-span-4 pl-4 py-2">
        <Bar className="h-5 w-28" />
        <Bar className="h-4 w-20" />
        <div className="h-px bg-neutral-200 my-1" />
        <Bar className="h-10 w-full" />
      </div>
    </div>
  </div>
);

const tocSkeleton = () => {
  const rows = [
    { depth: 0, w: 70 },
    { depth: 1, w: 60 },
    { depth: 1, w: 55 },
    { depth: 0, w: 65 },
    { depth: 1, w: 50 },
    { depth: 2, w: 45 },
    { depth: 0, w: 72 },
  ];
  return (
    <div className="flex flex-col gap-2">
      {rows.map((r, i) => (
        <div
          key={i}
          className="flex flex-row items-center gap-2"
          style={{ paddingLeft: `${r.depth * 16}px` }}
        >
          <Bar className="h-3 w-6" />
          <Bar className="h-3" style={{ width: `${r.w}%` }} />
        </div>
      ))}
    </div>
  );
};

const feedbackSkeleton = () => (
  <div className="flex flex-col gap-6">
    <div className="flex flex-col gap-4">
      {["Understanding", "Actionability", "Relevance", "Overall Rating"].map(
        (_, i) => (
          <div key={i} className="flex flex-col gap-2">
            <div className="flex flex-row justify-between">
              <Bar className="h-3 w-24" />
              <Bar className="h-3 w-6" />
            </div>
            <div className="relative w-full h-1.5 bg-neutral-100 rounded-full">
              <Bar
                className="h-full rounded-full"
                style={{ width: `${30 + i * 15}%` }}
              />
              <Bar
                className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full"
                style={{ left: `calc(${30 + i * 15}% - 8px)` }}
              />
            </div>
          </div>
        ),
      )}
    </div>

    <div className="flex flex-col gap-1">
      <Bar className="h-3 w-40" />
      <Bar className="h-20 w-full rounded-md" />
    </div>

    <div className="flex flex-col gap-1">
      <Bar className="h-3 w-24" />
      <Bar className="h-9 w-full rounded-md" />
    </div>

    <Bar className="h-10 w-full rounded-md" />
  </div>
);

const scatterChartSkeleton = () => (
  <div className="flex w-full">
    <div className="flex flex-col gap-5 pr-2 py-2 shrink-0">
      {Array.from({ length: 5 }).map((_, i) => (
        <Bar key={i} className="h-2 w-6" />
      ))}
    </div>
    <div className="flex-1 border-l border-neutral-200 flex flex-col">
      <div className="relative flex-1 min-h-[180px]">
        {[
          { l: 20, t: 60 }, { l: 35, t: 30 }, { l: 45, t: 70 },
          { l: 55, t: 20 }, { l: 65, t: 55 }, { l: 75, t: 40 },
          { l: 85, t: 25 }, { l: 30, t: 45 }, { l: 60, t: 65 },
        ].map((p, i) => (
          <Bar
            key={i}
            className="absolute w-2.5 h-2.5 rounded-full"
            style={{ left: `${p.l}%`, top: `${p.t}%` }}
          />
        ))}
      </div>
      <div className="flex flex-row justify-between px-2 pb-1 gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <Bar key={i} className="h-2 w-8" />
        ))}
      </div>
    </div>
  </div>
);

const areaChartSkeleton = () => (
  <div className="flex w-full">
    <div className="flex flex-col gap-5 pr-2 py-2 shrink-0">
      {Array.from({ length: 5 }).map((_, i) => (
        <Bar key={i} className="h-2 w-6" />
      ))}
    </div>
    <div className="flex-1 border-l border-neutral-200 flex flex-col">
      <div className="relative flex-1 min-h-[180px] overflow-hidden">
        <div
          className="absolute inset-x-0 bottom-0 bg-neutral-200/70 animate-pulse"
          style={{
            clipPath:
              "polygon(0% 100%, 0% 60%, 15% 45%, 30% 55%, 50% 30%, 70% 45%, 85% 25%, 100% 40%, 100% 100%)",
            height: "100%",
          }}
        />
      </div>
      <div className="flex flex-row justify-between px-2 pb-1 gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <Bar key={i} className="h-2 w-8" />
        ))}
      </div>
    </div>
  </div>
);

const radialBarSkeleton = () => (
  <div className="flex w-full items-center justify-center py-4">
    <div className="relative w-[180px] h-[180px]">
      <Bar className="absolute inset-0 rounded-full opacity-40" />
      <Bar className="absolute inset-[12%] rounded-full opacity-60" />
      <Bar className="absolute inset-[24%] rounded-full opacity-80" />
      <div className="absolute inset-[38%] rounded-full bg-white" />
    </div>
  </div>
);

const progressStagesSkeleton = (steps = 6) => (
  <div className="w-full overflow-hidden">
    <div
      className="flex flex-row items-center gap-1"
      style={{ minWidth: `${steps * 110}px` }}
    >
      {Array.from({ length: steps }).map((_, i) => (
        <Bar
          key={i}
          className="h-14 flex-1"
          style={{ clipPath: "polygon(0 0, 88% 0, 100% 50%, 88% 100%, 0 100%, 12% 50%)" }}
        />
      ))}
    </div>
  </div>
);

const forkJoinSkeleton = () => (
  <div className="w-full min-h-[200px] flex flex-col gap-4">
    <div className="flex flex-row items-center justify-around">
      <Bar className="w-3 h-3 rounded-full" />
      <Bar className="w-3 h-3 rounded-full" />
      <Bar className="w-3 h-3 rotate-45 rounded-sm" />
      <Bar className="w-3 h-3 rounded-full" />
    </div>
    <div className="flex flex-row items-center justify-around opacity-70">
      <div />
      <Bar className="w-3 h-3 rounded-full" />
      <div />
      <Bar className="w-3 h-3 rounded-full" />
    </div>
    <div className="flex flex-row items-center justify-around">
      <div />
      <div />
      <Bar className="w-3 h-3 rotate-45 rounded-sm" />
      <Bar className="w-3 h-3 rounded-full" />
    </div>
  </div>
);

const ganttPhasesSkeleton = (rows = 5) => (
  <div className="w-full flex flex-col gap-2 py-2">
    <div className="flex flex-row gap-4 pl-32">
      {Array.from({ length: 6 }).map((_, i) => (
        <Bar key={i} className="h-2 flex-1" />
      ))}
    </div>
    {Array.from({ length: rows }).map((_, i) => {
      const starts = [0, 15, 30, 45, 60];
      const widths = [50, 60, 45, 55, 40];
      return (
        <div key={i} className="flex flex-row items-center gap-2">
          <Bar className="h-3 w-28 shrink-0" />
          <div className="flex-1 h-4 relative">
            <Bar
              className="absolute h-4 rounded-sm"
              style={{ left: `${starts[i % starts.length]}%`, width: `${widths[i % widths.length]}%` }}
            />
          </div>
        </div>
      );
    })}
  </div>
);

const parallelStreamsSkeleton = (tracks = 3) => (
  <div className="w-full flex flex-col gap-6 py-4">
    {Array.from({ length: tracks }).map((_, i) => (
      <div key={i} className="flex flex-row items-center gap-3">
        <Bar className="h-3 w-16 shrink-0" />
        <div className="flex-1 h-[2px] bg-neutral-200 relative">
          {[10, 30, 50, 70, 90].map((p) => (
            <Bar
              key={p}
              className="absolute w-2.5 h-2.5 rounded-full -translate-y-1/2 top-1/2"
              style={{ left: `${p}%` }}
            />
          ))}
        </div>
      </div>
    ))}
  </div>
);

const segmentedProgressSkeleton = () => (
  <div className="w-full flex flex-col gap-2 py-4">
    <div className="flex flex-row gap-1 h-8">
      <Bar className="rounded-md" style={{ width: "15%" }} />
      <Bar className="rounded-md" style={{ width: "25%" }} />
      <Bar className="rounded-md" style={{ width: "35%" }} />
      <Bar className="rounded-md" style={{ width: "15%" }} />
      <Bar className="rounded-md" style={{ width: "10%" }} />
    </div>
    <div className="flex flex-row gap-1">
      <Bar className="h-2 rounded-md" style={{ width: "15%" }} />
      <Bar className="h-2 rounded-md" style={{ width: "25%" }} />
      <Bar className="h-2 rounded-md" style={{ width: "35%" }} />
      <Bar className="h-2 rounded-md" style={{ width: "15%" }} />
      <Bar className="h-2 rounded-md" style={{ width: "10%" }} />
    </div>
  </div>
);

const stateMachineSkeleton = () => (
  <div className="w-full min-h-[200px] flex flex-col gap-6 py-4">
    <div className="flex flex-row items-center justify-around">
      <Bar className="w-12 h-12 rounded-full" />
      <Bar className="w-12 h-12 rounded-full" />
      <Bar className="w-12 h-12 rounded-full" />
      <Bar className="w-12 h-12 rounded-full" />
    </div>
    <div className="flex flex-row items-center justify-center">
      <Bar className="w-12 h-12 rounded-full" />
    </div>
  </div>
);

const viewToggleRowSkeleton = () => (
  <div className="flex h-8 items-center justify-end">
    <Bar className="h-7 w-28" />
  </div>
);

const withViewToggle = (
  inner: JSX.Element,
  view3d: boolean,
  gapClassName = "gap-3",
) =>
  view3d ? (
    <div className={cn("flex w-full flex-col", gapClassName)}>
      {viewToggleRowSkeleton()}
      {inner}
    </div>
  ) : (
    inner
  );

const QUADRANT_SKELETON_DOTS = [
  [30, 40],
  [60, 25],
  [45, 65],
];

const quadrantMatrixSkeleton = (
  quadrants: QuadrantKey[],
  collapsed: boolean,
  showLegend: boolean,
) => (
  <div className="flex w-full flex-col gap-4">
    <div
      className={cn(
        "relative w-full grid grid-cols-2 gap-1",
        collapsed ? "grid-rows-1" : "grid-rows-2",
      )}
      style={{
        height: collapsed
          ? CHART_PLOT_HEIGHT.quadrantCollapsed
          : CHART_PLOT_HEIGHT.quadrant,
      }}
    >
      {Array.from({ length: collapsed ? 2 : 4 }).map((_, i) => (
        <div key={i} className="relative border border-dashed border-neutral-200 rounded-md">
          {QUADRANT_SKELETON_DOTS.map(([x, y], j) => (
            <Bar
              key={j}
              className="absolute w-2.5 h-2.5 rounded-full"
              style={{ left: `${x}%`, top: `${y}%` }}
            />
          ))}
        </div>
      ))}
    </div>
    {showLegend && quadrants.length > 0 && (
      <div className="flex flex-row gap-1">
        {quadrants.map((q) => (
          <Bar key={q} className="h-6 w-24" />
        ))}
      </div>
    )}
  </div>
);

const quadrantSkeletonFor = (data: unknown, showLegend: boolean) => {
  const items = Array.isArray((data as { items?: unknown } | undefined)?.items)
    ? ((data as { items: { effort?: unknown; impact?: unknown }[] }).items)
    : [];
  const present = new Set(
    items.map((it) => quadrantOf(Number(it?.effort), Number(it?.impact))),
  );
  const hasTop = present.has("quick-wins") || present.has("big-bets");
  const hasBottom = present.has("fill-ins") || present.has("time-sinks");
  const quadrants = items.length > 0 ? QUADRANT_ORDER.filter((q) => present.has(q)) : QUADRANT_ORDER;
  return quadrantMatrixSkeleton(quadrants, hasTop !== hasBottom, showLegend);
};

const correlationHeatmapSkeleton = (featureCount: number, view3d: boolean) => {
  const n = Math.max(1, featureCount);
  const { matrixPx, width, height } = correlationFrameSize(n);
  return (
    <div className="flex w-full flex-col gap-3">
      <div
        className={cn(
          "flex items-center justify-between gap-3",
          view3d ? "min-h-8" : "min-h-[1.25rem]",
        )}
      >
        <Bar className="h-3 w-40" />
        {view3d && <Bar className="h-7 w-28 shrink-0" />}
      </div>
      <div className="relative shrink-0" style={{ width, height }}>
        <div
          className="absolute grid gap-1"
          style={{
            left: CORRELATION_FRAME_PAD.left,
            top: CORRELATION_FRAME_PAD.top,
            width: matrixPx,
            height: matrixPx,
            gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))`,
            gridTemplateRows: `repeat(${n}, minmax(0, 1fr))`,
          }}
        >
          {Array.from({ length: n * n }).map((_, i) => (
            <Bar key={i} className="rounded-sm" style={{ opacity: 0.3 + ((i * 7) % 70) / 100 }} />
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Bar className="h-3 w-16" />
        <Bar className="h-2 w-24 rounded-full" />
      </div>
    </div>
  );
};

const correlationFeatureCount = (data: unknown): number => {
  if (data === undefined || data === null) return CORRELATION_DEFAULT_FEATURE_COUNT;
  const features = (data as { features?: unknown }).features;
  return Array.isArray(features) ? features.length : 0;
};

const pagerSkeleton = () => (
  <div className="flex h-8 items-center justify-between gap-3">
    <Bar className="h-3 w-20" />
    <div className="flex items-center gap-2">
      <Bar className="size-8 rounded-md" />
      <Bar className="h-3 w-16" />
      <Bar className="size-8 rounded-md" />
    </div>
  </div>
);

const featureComparisonSkeleton = () => (
  <div className="w-full flex flex-col gap-3">
    <Bar className="h-3 w-1/2" />
    <div className="flex flex-col">
      <div className="grid grid-cols-5 gap-2 py-4 border-b border-dashed border-neutral-200">
        <div />
        <Bar className="h-4" />
        <Bar className="h-4" />
        <Bar className="h-4" />
        <Bar className="h-4" />
      </div>
      {Array.from({ length: FEATURE_COMPARISON_ROWS_PER_PAGE }).map((_, i) => (
        <div key={i} className="grid h-13 grid-cols-5 items-center gap-2 border-b border-dashed border-neutral-200">
          <Bar className="h-3" />
          <Bar className="h-3 w-8 mx-auto rounded-full" />
          <Bar className="h-3 w-8 mx-auto rounded-full" />
          <Bar className="h-3 w-8 mx-auto rounded-full" />
          <Bar className="h-3 w-8 mx-auto rounded-full" />
        </div>
      ))}
    </div>
    {pagerSkeleton()}
  </div>
);

const circularLoopSkeleton = () => (
  <div className="w-full flex items-center justify-center py-4">
    <div className="relative h-40 w-40">
      <Bar className="absolute inset-0 rounded-full" />
      {[0, 1, 2, 3].map((i) => {
        const angle = (i / 4) * 360 - 90;
        const rad = (angle * Math.PI) / 180;
        const x = 50 + 42 * Math.cos(rad);
        const y = 50 + 42 * Math.sin(rad);
        return (
          <Bar
            key={i}
            className="absolute h-6 w-6 rounded-full"
            style={{ left: `${x}%`, top: `${y}%`, transform: "translate(-50%, -50%)" }}
          />
        );
      })}
    </div>
  </div>
);

const ecosystemMapSkeleton = () => (
  <div className="w-full flex items-center justify-center py-4">
    <div className="relative h-56 w-56">
      <Bar className="absolute inset-0 rounded-full opacity-30" />
      <Bar className="absolute inset-6 rounded-full opacity-50" />
      <Bar className="absolute inset-14 rounded-full opacity-70" />
      <Bar className="absolute inset-20 rounded-full" />
    </div>
  </div>
);

const hierarchyTreeSkeleton = () => (
  <div className="w-full flex flex-col gap-2 py-2">
    <div className="flex justify-center">
      <Bar className="h-6 w-32 rounded-md" />
    </div>
    <div className="flex justify-around">
      {Array.from({ length: 3 }).map((_, i) => (
        <Bar key={i} className="h-5 w-24 rounded-md" />
      ))}
    </div>
    <div className="grid grid-cols-6 gap-2">
      {Array.from({ length: 9 }).map((_, i) => (
        <Bar key={i} className="h-4 rounded-md" />
      ))}
    </div>
  </div>
);

const parallelCategoryTreesSkeleton = () => (
  <div className="w-full grid grid-cols-2 gap-4 py-2">
    {Array.from({ length: 2 }).map((_, tree) => (
      <div key={tree} className="flex flex-col gap-2">
        <Bar className="h-6 rounded-md" />
        <div className="grid grid-cols-3 gap-1">
          {Array.from({ length: 3 }).map((_, i) => (
            <Bar key={i} className="h-5 rounded-md" />
          ))}
        </div>
      </div>
    ))}
  </div>
);

const activityFeedSkeleton = () => (
  <div className="w-full grid grid-cols-3 gap-x-4 gap-y-1 py-2">
    {Array.from({ length: 9 }).map((_, i) => (
      <div key={i} className="flex items-start gap-2">
        <Bar className="h-6 w-6 rounded-md" />
        <div className="flex-1 flex flex-col gap-1">
          <Bar className="h-3 rounded" style={{ width: "80%" }} />
          <Bar className="h-2 rounded" style={{ width: "60%" }} />
        </div>
      </div>
    ))}
  </div>
);

const metricListSkeleton = () => (
  <div className="w-full grid grid-cols-3 gap-x-4 gap-y-2 py-2">
    {Array.from({ length: 9 }).map((_, i) => (
      <div key={i} className="flex flex-col gap-1">
        <div className="flex items-center gap-1">
          <Bar className="h-3 w-3" />
          <Bar className="h-3 rounded" style={{ width: "50%" }} />
        </div>
        <Bar className="h-4 rounded" style={{ width: "70%" }} />
      </div>
    ))}
  </div>
);

const deltaComparisonSkeleton = () => (
  <div className="w-full flex flex-col gap-2 py-2">
    <div className="flex items-center justify-between border-b border-dashed border-neutral-200 pb-2">
      <Bar className="h-3 rounded" style={{ width: "20%" }} />
      <Bar className="h-3 rounded" style={{ width: "12%" }} />
    </div>
    <div className="grid grid-cols-3 gap-x-4 gap-y-2">
      {Array.from({ length: 9 }).map((_, i) => (
        <div key={i} className="flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <Bar className="h-3 rounded" style={{ width: "55%" }} />
            <Bar className="h-3 rounded" style={{ width: "24%" }} />
          </div>
          <div className="flex items-center gap-2">
            <Bar className="h-3 rounded" style={{ width: "28%" }} />
            <Bar className="h-3 rounded" style={{ width: "8%" }} />
            <Bar className="h-4 rounded" style={{ width: "34%" }} />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const splitComparisonSkeleton = () => (
  <div className="w-full">
    <div
      className="grid w-max"
      style={{
        gridTemplateColumns: `120px repeat(5, 120px)`,
      }}
    >
      <div />
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={`h-${i}`}
          className="flex flex-col gap-0.5 px-3 pb-2 border-l border-dashed border-neutral-200"
        >
          <Bar className="h-3 rounded" style={{ width: "55%" }} />
          <Bar className="h-2 rounded" style={{ width: "38%" }} />
        </div>
      ))}
      {Array.from({ length: 2 }).map((_, ei) => (
        <Fragment key={`row-${ei}`}>
          <div
            className={cn(
              "flex flex-col justify-center gap-0.5 px-3 py-3",
              ei > 0 && "border-t border-dashed border-neutral-200",
            )}
          >
            <Bar className="h-3 rounded" style={{ width: "70%" }} />
          </div>
          {Array.from({ length: 5 }).map((_, ci) => (
            <div
              key={`c-${ei}-${ci}`}
              className={cn(
                "flex items-center px-3 py-3 border-l border-dashed border-neutral-200",
                ei > 0 && "border-t border-dashed border-neutral-200",
              )}
            >
              <Bar className="h-4 rounded" style={{ width: "45%" }} />
            </div>
          ))}
        </Fragment>
      ))}
    </div>
  </div>
);

const skillLevelMatrixSkeleton = () => (
  <div className="w-full flex flex-col gap-1 py-2">
    {Array.from({ length: 5 }).map((_, r) => (
      <div key={r} className="grid grid-cols-7 gap-2 items-center">
        <Bar className="h-3 col-span-2" />
        {Array.from({ length: 5 }).map((_, c) => (
          <div key={c} className="flex flex-row items-center gap-0.5 justify-center">
            {Array.from({ length: 5 }).map((_, d) => (
              <Bar key={d} className="w-1.5 h-1.5 rounded-full" />
            ))}
          </div>
        ))}
      </div>
    ))}
  </div>
);

const timelineStepperSkeleton = (steps = 6) => (
  <div className="w-full flex flex-col gap-4 py-4">
    <div className="flex flex-row justify-between px-4">
      {Array.from({ length: steps }).map((_, i) => (
        <Bar key={i} className="h-2.5 w-14" />
      ))}
    </div>
    <div className="relative h-3 flex items-center">
      <div className="absolute inset-x-4 h-[2px] bg-neutral-200" />
      <div className="relative w-full flex flex-row justify-between px-4">
        {Array.from({ length: steps }).map((_, i) => (
          <Bar key={i} className="w-3 h-3 rounded-full" />
        ))}
      </div>
    </div>
    <div className="flex flex-row justify-between px-4">
      {Array.from({ length: steps }).map((_, i) => (
        <Bar key={i} className="h-2 w-10" />
      ))}
    </div>
  </div>
);

const pyramidSkeleton = () => (
  <div className="w-full flex flex-col items-center gap-1 py-2">
    <Bar className="h-9 rounded-t-md" style={{ width: "30%" }} />
    <Bar className="h-9 rounded-none" style={{ width: "50%" }} />
    <Bar className="h-9 rounded-none" style={{ width: "70%" }} />
    <Bar className="h-9 rounded-b-md" style={{ width: "90%" }} />
  </div>
);

const socialProofStatsSkeleton = () => (
  <div className="w-full grid grid-cols-2 sm:grid-cols-4 gap-3">
    {Array.from({ length: 4 }).map((_, i) => (
      <div
        key={i}
        className="flex flex-col gap-3 p-4 rounded-xl border border-neutral-200"
      >
        <Bar className="h-3 w-1/2" />
        <Bar className="h-8 w-3/4" />
        <Bar className="h-2 w-2/3" />
        <Bar className="h-10 w-full mt-2" />
      </div>
    ))}
  </div>
);

const beforeAfterAnnotatedSkeleton = () => (
  <div className="w-full flex flex-col gap-4">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-1 rounded-lg bg-neutral-100 p-0.5">
        <div className="h-7 w-16 rounded-md bg-white" />
        <div className="h-7 w-16 rounded-md" />
      </div>
      <Bar className="h-3 w-24" />
    </div>
    <div className="relative w-full rounded-xl bg-neutral-200/70 animate-pulse" style={{ aspectRatio: "16 / 10" }}>
      {[
        { top: "30%", left: "20%" },
        { top: "35%", left: "70%" },
        { top: "75%", left: "25%" },
      ].map((p, i) => (
        <div
          key={i}
          className="absolute h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full bg-neutral-400/70"
          style={{ top: p.top, left: p.left }}
        />
      ))}
    </div>
    <div className="flex flex-col gap-2 border-t border-dashed border-neutral-200 pt-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="h-5 w-5 rounded-full bg-neutral-200" />
          <Bar className="h-3 flex-1" />
          <Bar className="h-3 w-24" />
        </div>
      ))}
    </div>
  </div>
);

const beforeAfterSliderSkeleton = () => (
  <div className="w-full flex flex-col gap-4">
    <div className="relative w-full rounded-xl bg-neutral-200/70 animate-pulse overflow-hidden" style={{ aspectRatio: "16 / 10" }}>
      <div className="absolute inset-y-0 left-1/2 w-px bg-neutral-400">
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-9 w-9 rounded-full bg-white ring-1 ring-neutral-300" />
      </div>
    </div>
    <div className="flex items-center justify-between gap-3 border-t border-dashed border-neutral-200 pt-4">
      <div className="flex gap-1">
        {Array.from({ length: 3 }).map((_, i) => (
          <Bar key={i} className="h-7 w-16 rounded-md" />
        ))}
      </div>
      <Bar className="h-3 w-40" />
    </div>
  </div>
);

const logoWallSkeleton = () => (
  <div className="w-full flex flex-col gap-4">
    <div className="flex items-end justify-between gap-5">
      <div className="flex flex-col gap-2">
        <Bar className="h-10 w-24" />
        <Bar className="h-3 w-48" />
        <Bar className="h-3 w-32" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-neutral-200" />
            <Bar className="h-3 w-20" />
          </div>
        ))}
      </div>
    </div>
    <div className="border-t border-dashed border-neutral-200 pt-5">
      <div className="flex gap-2 overflow-hidden">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="h-[68px] min-w-[168px] flex-1 rounded-lg border border-dashed border-neutral-200 flex items-center justify-center gap-2 px-5"
          >
            <div className="h-4 w-4 rounded-full bg-neutral-200" />
            <Bar className="h-3 w-20" />
          </div>
        ))}
      </div>
    </div>
    <Bar className="h-3 w-64" />
  </div>
);

const roiCalculatorSkeleton = () => (
  <div className="w-full grid grid-cols-2 gap-6">
    <div className="flex flex-col gap-5">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex flex-col gap-2">
          <div className="flex justify-between">
            <Bar className="h-3 w-1/3" />
            <Bar className="h-3 w-1/5" />
          </div>
          <div className="h-1 rounded-full bg-neutral-200/70 animate-pulse" />
        </div>
      ))}
    </div>
    <div className="rounded-xl border border-neutral-200 p-5 flex flex-col gap-3">
      <Bar className="h-3 w-1/2" />
      <Bar className="h-10 w-2/3" />
      <Bar className="h-2 w-1/3" />
      <div className="grid grid-cols-3 gap-3 border-t border-dashed border-neutral-200 pt-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex flex-col gap-1">
            <Bar className="h-2 w-3/4" />
            <Bar className="h-3 w-1/2" />
          </div>
        ))}
      </div>
      <div className="mt-2 flex flex-col gap-3 border-t border-dashed border-neutral-200 pt-4">
        <Bar className="h-3 w-full" />
        <Bar className="h-3 w-4/5" />
      </div>
    </div>
  </div>
);

const pricingTiersSkeleton = () => (
  <div className="w-full flex flex-col gap-4">
    <Bar className="h-7 w-40 rounded-md" />
    <div className="grid grid-cols-3 gap-3">
      {Array.from({ length: 3 }).map((_, col) => (
        <div
          key={col}
          className="flex flex-col gap-3 rounded-xl border border-neutral-200 p-4"
        >
          <Bar className="h-3 w-1/2" />
          <Bar className="h-3 w-3/4" />
          <Bar className="h-8 w-2/3 mt-2" />
          <Bar className="h-2 w-1/2" />
          <div className="mt-3 border-t border-dashed border-neutral-200" />
          <div className="flex flex-col gap-2 mt-1">
            {Array.from({ length: 6 }).map((_, row) => (
              <div key={row} className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-neutral-200" />
                <Bar className="h-2 flex-1" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const keywordsGroupedSkeleton = () => (
  <div className="w-full flex flex-col gap-4">
    <div className="grid grid-flow-col auto-cols-[220px] gap-x-6">
      {Array.from({ length: 4 }).map((_, col) => (
        <div key={col} className="flex flex-col gap-3">
          <div className="flex items-baseline justify-between border-b border-dashed border-neutral-200 pb-2">
            <Bar className="h-3 w-20" />
            <Bar className="h-2 w-4" />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {Array.from({ length: 5 }).map((_, i) => (
              <Bar
                key={i}
                className="h-6 rounded-md"
                style={{ width: `${40 + ((i * 17) % 60)}px` }}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
    <div className="mt-2 flex items-center gap-3 border-t border-dashed border-neutral-200 pt-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <div className="h-3.5 w-5 rounded-[4px] bg-neutral-200" />
          <Bar className="h-2 w-16" />
          <Bar className="h-2 w-4" />
        </div>
      ))}
    </div>
  </div>
);

const keywordsExplainedSkeleton = () => (
  <div className="w-full flex flex-col gap-4">
    <div className="flex flex-row gap-2">
      {Array.from({ length: 3 }).map((_, i) => (
        <Bar key={i} className="h-8 w-24 rounded-md" />
      ))}
    </div>
    <div className="flex flex-wrap gap-1.5">
      {Array.from({ length: 12 }).map((_, i) => (
        <Bar
          key={i}
          className="h-8 rounded-md"
          style={{ width: `${60 + ((i * 23) % 80)}px` }}
        />
      ))}
    </div>
    <div className="flex flex-col gap-3 rounded-xl border border-neutral-200 p-5">
      <Bar className="h-6 w-1/3" />
      <Bar className="h-3 w-1/4" />
      <Bar className="h-3 w-full mt-2" />
      <Bar className="h-3 w-11/12" />
      <Bar className="h-3 w-5/6" />
      <div className="mt-2 flex items-center gap-2">
        <Bar className="h-4 w-14 rounded" />
        <Bar className="h-4 w-14 rounded" />
        <Bar className="h-4 w-14 rounded" />
      </div>
    </div>
  </div>
);

const keyTakeawaysSkeleton = () => (
  <div className="w-full overflow-hidden">
    <div className="grid grid-flow-col grid-rows-[repeat(2,auto)] gap-x-6 auto-cols-[minmax(15rem,20rem)]">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className={cn(
            "grid grid-cols-[32px_1fr_auto] items-start gap-x-3 py-4",
            i % 2 !== 0 && "border-t border-dashed border-neutral-200",
          )}
        >
          <Bar className="h-6 w-7 rounded" />
          <div className="flex flex-col gap-2">
            <Bar className="h-3.5 rounded" style={{ width: `${58 + ((i * 11) % 30)}%` }} />
            <Bar className="h-3 rounded" style={{ width: `${72 + ((i * 7) % 22)}%` }} />
          </div>
          <Bar className="h-5 w-16 rounded-md" />
        </div>
      ))}
    </div>
  </div>
);

const faqSkeleton = () => (
  <div className="w-full flex flex-col gap-2">
    <div className="flex flex-row gap-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <Bar key={i} className="h-8 w-20 rounded-md" />
      ))}
    </div>
    <div className="flex flex-col">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className={cn(
            "flex flex-col gap-2 py-4",
            i > 0 && "border-t border-dashed border-neutral-200",
          )}
        >
          <div className="flex items-center justify-between gap-4">
            <Bar className="h-3.5 rounded" style={{ width: `${46 + ((i * 13) % 34)}%` }} />
            <Bar className="h-4 w-4 rounded" />
          </div>
          {i === 0 && (
            <>
              <Bar className="h-3 w-11/12 rounded" />
              <Bar className="h-3 w-4/6 rounded" />
            </>
          )}
        </div>
      ))}
    </div>
  </div>
);

const bigNumberChartSkeleton = () => (
  <div className="w-full flex flex-col gap-3">
    <Bar className="h-3 w-1/4" />
    <Bar className="h-16 w-1/2" />
    <Bar className="h-3 w-3/4" />
    <Bar className="h-3 w-2/5" />
    <div className="mt-2 flex w-full">
      <div className="flex flex-col gap-5 pr-2 py-2 shrink-0">
        {Array.from({ length: 5 }).map((_, i) => (
          <Bar key={i} className="h-2 w-6" />
        ))}
      </div>
      <div className="flex-1 border-l border-neutral-200 flex flex-col">
        <div className="relative flex-1 min-h-[180px] overflow-hidden">
          <div
            className="absolute inset-x-0 bottom-0 bg-neutral-200/70 animate-pulse"
            style={{
              clipPath:
                "polygon(0% 100%, 0% 65%, 15% 60%, 30% 50%, 50% 45%, 70% 32%, 85% 22%, 100% 12%, 100% 100%)",
              height: "100%",
            }}
          />
        </div>
        <div className="flex flex-row justify-between px-2 pb-1 gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Bar key={i} className="h-2 w-8" />
          ))}
        </div>
      </div>
    </div>
  </div>
);

const listCardsSkeleton = (count = 3) => (
  <div className="w-full grid grid-cols-1 sm:grid-cols-3 gap-4">
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className="flex flex-col rounded-xl border border-neutral-200 overflow-hidden"
      >
        <div className="h-24 flex items-center justify-center border-b border-dashed border-neutral-200">
          <Bar className="w-8 h-8 rounded-md" />
        </div>
        <div className="flex flex-col gap-2 p-4">
          <Bar className="h-3 w-2/3" />
          <Bar className="h-2 w-full" />
          <Bar className="h-2 w-4/5" />
        </div>
      </div>
    ))}
  </div>
);

const dottedMapSkeleton = () => (
  <div className="w-full relative min-h-[180px] rounded-md bg-neutral-50 overflow-hidden">
    <div className="absolute inset-0 grid grid-cols-16 grid-rows-8 gap-1 p-2 opacity-40">
      {Array.from({ length: 128 }).map((_, i) => (
        <div
          key={i}
          className="w-1 h-1 rounded-full bg-neutral-300"
          style={{ opacity: 0.3 + ((i * 7) % 70) / 100 }}
        />
      ))}
    </div>
    {[
      { l: 25, t: 40 }, { l: 45, t: 30 }, { l: 65, t: 55 },
      { l: 30, t: 65 }, { l: 75, t: 35 },
    ].map((p, i) => (
      <Bar
        key={i}
        className="absolute w-2 h-2 rounded-full"
        style={{ left: `${p.l}%`, top: `${p.t}%` }}
      />
    ))}
  </div>
);

const wrap = (inner: JSX.Element, withHeading = true) => (
  <div className="flex flex-col gap-2">
    {withHeading && headingSkeleton()}
    {inner}
  </div>
);

const candlestickSkeleton = () => (
  <div className="flex w-full items-end gap-1.5 min-h-[220px] px-2 pt-4">
    {Array.from({ length: 14 }).map((_, i) => {
      const bodyH = 20 + ((i * 17) % 55);
      const wickH = bodyH + 20 + ((i * 7) % 22);
      const positive = i % 3 !== 1;
      return (
        <div key={i} className="flex flex-col items-center flex-1">
          <Bar
            className="w-[2px]"
            style={{ height: `${wickH}px`, opacity: positive ? 0.7 : 0.5 }}
          />
          <Bar
            className={`w-full ${positive ? "opacity-90" : "opacity-60"}`}
            style={{ height: `${bodyH}px` }}
          />
        </div>
      );
    })}
  </div>
);

const funnelSkeleton = () => (
  <div className="flex w-full flex-col gap-3">
    <div
      className="flex w-full items-center justify-center"
      style={{ height: CHART_PLOT_HEIGHT.funnel }}
    >
      <div
        className="flex w-full max-h-full flex-row items-center gap-0.5"
        style={{ aspectRatio: "2.2 / 1" }}
      >
        {[100, 78, 60, 42, 24].map((h, i) => (
          <Bar
            key={i}
            className="flex-1 rounded-sm"
            style={{ height: `${h}%`, opacity: 1 - i * 0.12 }}
          />
        ))}
      </div>
    </div>
    <div className="flex w-full flex-row gap-6 overflow-hidden pb-1">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex shrink-0 items-center gap-2">
          <Bar className="size-2.5 rounded-sm" />
          <Bar className="h-2.5 w-14" />
        </div>
      ))}
    </div>
  </div>
);

const heatmapSkeleton = () => (
  <div className="w-full grid grid-cols-12 gap-1 min-h-[180px]">
    {Array.from({ length: 12 * 7 }).map((_, i) => (
      <Bar
        key={i}
        className="aspect-square rounded-sm"
        style={{ opacity: 0.15 + ((i * 13) % 85) / 100 }}
      />
    ))}
  </div>
);

const profitLossSkeleton = () => (
  <div className="flex w-full items-center min-h-[180px] px-4 relative">
    <div className="absolute inset-x-4 top-1/2 h-px bg-neutral-200" />
    <div className="flex-1 flex items-center gap-2">
      {Array.from({ length: 12 }).map((_, i) => {
        const positive = i % 2 === 0;
        const h = 12 + ((i * 11) % 40);
        return (
          <Bar
            key={i}
            className="w-full rounded-sm"
            style={{
              height: `${h}px`,
              transform: positive
                ? `translateY(-${h / 2}px)`
                : `translateY(${h / 2}px)`,
              opacity: 0.6,
            }}
          />
        );
      })}
    </div>
  </div>
);

const radarSkeleton = () => (
  <div className="flex w-full items-center justify-center py-4">
    <div className="relative w-[220px] h-[220px]">
      {[100, 75, 50, 25].map((s, i) => (
        <div
          key={i}
          className="absolute inset-0 rounded-full border border-neutral-200"
          style={{ transform: `scale(${s / 100})`, opacity: 0.5 }}
        />
      ))}
      <Bar className="absolute inset-[15%] rounded-full opacity-40" />
    </div>
  </div>
);

const sankeySkeleton = () => (
  <div className="flex w-full min-h-[220px] items-stretch gap-8 px-4 py-4">
    <div className="flex flex-col justify-around">
      <Bar className="h-8 w-16 rounded-sm" />
      <Bar className="h-8 w-16 rounded-sm" />
      <Bar className="h-8 w-16 rounded-sm" />
    </div>
    <div className="flex-1 flex items-center relative">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="absolute h-[2px] bg-neutral-200 w-full"
          style={{ top: `${20 + i * 12}%`, opacity: 0.5 }}
        />
      ))}
    </div>
    <div className="flex flex-col justify-around">
      <Bar className="h-10 w-16 rounded-sm" />
      <Bar className="h-10 w-16 rounded-sm" />
    </div>
  </div>
);

const sunburstSkeleton = () => (
  <div className="flex w-full items-center justify-center py-4">
    <div className="relative w-[220px] h-[220px]">
      <Bar className="absolute inset-0 rounded-full opacity-30" />
      <Bar className="absolute inset-[15%] rounded-full opacity-50" />
      <Bar className="absolute inset-[32%] rounded-full opacity-70" />
      <div className="absolute inset-[48%] rounded-full bg-white" />
    </div>
  </div>
);

const DATA_TABLE_SKELETON_COLUMNS =
  "minmax(10rem, 10fr) minmax(7rem, 7fr) minmax(7rem, 7fr) minmax(7rem, 7fr)";

const ringSkeletonPath = (
  <div
    className="relative shrink-0"
    style={{ width: RING_KPI_RING_SIZE, height: RING_KPI_RING_SIZE }}
  >
    <Bar className="absolute inset-0 rounded-full" />
    <div className="absolute inset-[4px] rounded-full bg-white" />
  </div>
);

const ringKpisSkeleton = () => (
  <div className="w-full min-w-0 overflow-hidden">
    <div
      className="grid gap-2"
      style={{
        gridAutoFlow: "column",
        gridTemplateRows: "repeat(2, auto)",
        gridAutoColumns: `minmax(${RING_KPI_TILE_MIN_WIDTH}, 1fr)`,
      }}
    >
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="flex min-w-0 flex-col gap-4 rounded-xl border border-neutral-200 p-5"
        >
          <Bar className="h-3.5 w-1/2" />
          <div className="flex items-center gap-3">
            {ringSkeletonPath}
            <Bar className="h-7 w-1/3" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Bar className="h-3 w-3/5" />
            <Bar className="h-3 w-2/5" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const metricTabsChartSkeleton = () => (
  <div className="flex w-full flex-col gap-4">
    <div className="flex w-full flex-row flex-nowrap overflow-hidden rounded-xl border border-neutral-200">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className={cn(
            "flex min-w-[8rem] flex-[1_0_auto] flex-col gap-1.5 px-3 py-2.5",
            i < 3 && "border-r border-neutral-200",
          )}
        >
          <Bar className="h-2.5 w-1/2" />
          <Bar className="h-4 w-3/4" />
        </div>
      ))}
    </div>
    <div className="relative overflow-hidden" style={{ height: CHART_PLOT_HEIGHT.metricTabs }}>
      <div className="absolute inset-0 flex flex-col justify-between py-3 opacity-30">
        {Array.from({ length: 4 }).map((_, i) => (
          <Bar key={i} className="h-px rounded-none" />
        ))}
      </div>
      <div
        className="absolute inset-x-0 bottom-7 top-3 bg-neutral-200/70 animate-pulse"
        style={{
          clipPath:
            "polygon(0% 100%, 0% 60%, 14% 55%, 28% 62%, 42% 45%, 57% 40%, 71% 30%, 85% 34%, 100% 18%, 100% 100%)",
        }}
      />
      <div className="absolute inset-x-0 bottom-0 flex justify-between">
        {Array.from({ length: 6 }).map((_, i) => (
          <Bar key={i} className="h-2 w-7" />
        ))}
      </div>
    </div>
    <div className="flex gap-4 border-t border-dashed border-neutral-200 pt-3">
      <Bar className="h-3 w-24" />
      <Bar className="h-3 w-20" />
    </div>
  </div>
);

const breakdownSkeleton = () => (
  <div className="flex w-full flex-col gap-5">
    <div className="flex flex-col gap-2">
      <Bar className="h-3 w-2/5" />
      <Bar className="h-9 w-1/2" />
    </div>
    <div className="flex flex-col gap-4 border-t border-dashed border-neutral-200 pt-5">
      {[92, 64, 48, 30, 18].map((w, i) => (
        <div key={i} className="flex flex-col gap-2">
          <div className="flex justify-between">
            <Bar className="h-3 w-1/3" />
            <Bar className="h-3 w-14" />
          </div>
          <div className="h-1.5 w-full rounded-full bg-neutral-100">
            <Bar className="h-full rounded-full" style={{ width: `${w}%` }} />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const dualMetricSkeleton = () => (
  <div className="flex w-full flex-col gap-5">
    <div className="flex items-center justify-between gap-3">
      <Bar className="h-3 w-1/4" />
      <Bar className="h-7 w-36 rounded-lg" />
    </div>
    <div className="flex flex-col gap-6">
      {[38, 62].map((fill, i) => (
        <div key={i} className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <Bar className="h-8 w-1/2" />
            <Bar className="h-5 w-10" />
          </div>
          <Bar className="h-3 w-1/3" />
          <div className="mt-2 flex h-4 gap-[3px]">
            {Array.from({ length: DUAL_METRIC_TICK_SEGMENTS }).map((_, t) => (
              <div
                key={t}
                className={cn(
                  "h-full flex-1 rounded-[1.5px]",
                  t < Math.round((fill / 100) * DUAL_METRIC_TICK_SEGMENTS)
                    ? "bg-neutral-200 animate-pulse"
                    : "bg-neutral-100",
                )}
              />
            ))}
          </div>
          <div className="mt-4 flex flex-col gap-1">
            <Bar className="h-2.5 w-1/5" />
            <Bar className="h-3 w-2/5" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const stageBarsSkeleton = () => (
  <div className="flex w-full flex-col gap-5">
    <div className="flex items-start justify-between gap-3">
      <div className="flex flex-1 flex-col gap-2">
        <Bar className="h-3 w-1/3" />
        <Bar className="h-8 w-2/5" />
        <Bar className="h-3 w-1/2" />
      </div>
      <Bar className="h-7 w-32 rounded-lg" />
    </div>
    <div className="grid grid-cols-[minmax(0,7rem)_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-3">
      {[100, 49, 23, 11, 6].map((w, i) => (
        <div key={i} className="contents">
          <Bar className="ml-auto h-3 w-3/4" />
          <div className="h-1.5 rounded-full bg-neutral-100">
            <Bar
              className="h-full rounded-full"
              style={{ width: `${Math.max(w, 3)}%`, opacity: 1 - i * 0.15 }}
            />
          </div>
          <Bar className="h-3 w-16" />
        </div>
      ))}
    </div>
    <div className="flex flex-col gap-2 border-t border-dashed border-neutral-200 pt-3">
      <Bar className="h-3 w-40" />
      <div className="flex flex-nowrap gap-3 overflow-hidden">
        {Array.from({ length: 5 }).map((_, i) => (
          <Bar key={i} className="h-3 w-20 shrink-0" />
        ))}
      </div>
    </div>
  </div>
);

const insightPanelSkeleton = () => (
  <div className="flex min-h-[22rem] w-full flex-col gap-6 rounded-2xl bg-neutral-100 p-6">
    <Bar className="h-6 w-24 rounded-md" />
    <div className="mt-auto flex flex-col gap-4">
      <Bar className="h-12 w-1/3" />
      <Bar className="h-5 w-11/12" />
      <Bar className="h-5 w-2/3" />
      <Bar className="h-3 w-4/5" />
    </div>
    <div className="flex gap-1.5">
      {Array.from({ length: 3 }).map((_, i) => (
        <Bar key={i} className="h-1 flex-1 rounded-full" />
      ))}
    </div>
  </div>
);

const dataTableSkeleton = () => (
  <div className="flex w-full flex-col gap-3">
    <div className="flex h-8 items-center gap-2">
      <Bar className="h-8 flex-1 rounded-md" />
      <Bar className="h-8 w-28 rounded-md" />
    </div>
    <div className="w-full overflow-hidden rounded-xl border border-neutral-200">
      <div
        className="grid h-9 items-center gap-3 border-b border-neutral-200 bg-neutral-50 px-3"
        style={{ gridTemplateColumns: DATA_TABLE_SKELETON_COLUMNS }}
      >
        <Bar className="h-2.5 w-1/2" />
        <Bar className="h-2.5 w-3/4" />
        <Bar className="ml-auto h-2.5 w-3/4" />
        <Bar className="ml-auto h-2.5 w-2/3" />
      </div>
      {Array.from({ length: DATA_TABLE_ROWS_PER_PAGE }).map((_, i) => (
        <div
          key={i}
          className="grid h-10 items-center gap-3 border-b border-dashed border-neutral-200 px-3 last:border-b-0"
          style={{ gridTemplateColumns: DATA_TABLE_SKELETON_COLUMNS }}
        >
          <Bar className="h-3" style={{ width: `${55 + ((i * 17) % 40)}%` }} />
          <Bar className="h-3 w-2/3" />
          <Bar className="ml-auto h-3 w-3/4" />
          <Bar className="ml-auto h-3 w-1/2" />
        </div>
      ))}
    </div>
    {pagerSkeleton()}
  </div>
);

type SkeletonModule = {
  type: string;
  view3d?: boolean;
  showLegend?: boolean;
  data?: unknown;
};

export const renderModuleSkeleton = (module: SkeletonModule): JSX.Element => {
  const view3d = Boolean(module.view3d);
  switch (module.type) {
    case "line":
      return wrap(lineChartSkeleton());
    case "bar":
      return wrap(withViewToggle(chartFrameSkeleton(), view3d));
    case "pie":
      return wrap(pieChartSkeleton());
    case "scatter":
      return wrap(withViewToggle(scatterChartSkeleton(), view3d));
    case "area":
      return wrap(areaChartSkeleton());
    case "radial-bar":
      return wrap(radialBarSkeleton());
    case "image":
      return imageSkeleton();
    case "progress":
      return wrap(progressSkeleton());
    case "quote":
      return wrap(quoteSkeleton());
    case "intro":
      return introSkeleton();
    case "toc":
      return wrap(tocSkeleton());
    case "feedback":
      return feedbackSkeleton();
    case "pyramid":
      return wrap(pyramidSkeleton());
    case "progress-stages":
      return wrap(progressStagesSkeleton());
    case "fork-join":
      return wrap(forkJoinSkeleton());
    case "gantt-phases":
      return wrap(ganttPhasesSkeleton());
    case "parallel-streams":
      return wrap(parallelStreamsSkeleton());
    case "segmented-progress":
      return wrap(segmentedProgressSkeleton());
    case "state-machine":
      return wrap(stateMachineSkeleton());
    case "timeline-stepper":
      return wrap(timelineStepperSkeleton());
    case "quadrant-matrix":
      return wrap(
        withViewToggle(
          quadrantSkeletonFor(module.data, module.showLegend !== false),
          view3d,
          "gap-4",
        ),
      );
    case "correlation-heatmap":
      return wrap(
        correlationHeatmapSkeleton(correlationFeatureCount(module.data), view3d),
      );
    case "feature-comparison":
      return wrap(featureComparisonSkeleton());
    case "table":
      return wrap(dataTableSkeleton());
    case "ring-kpis":
      return wrap(ringKpisSkeleton());
    case "metric-tabs-chart":
      return wrap(metricTabsChartSkeleton());
    case "breakdown":
      return wrap(breakdownSkeleton());
    case "dual-metric":
      return wrap(dualMetricSkeleton());
    case "stage-bars":
      return wrap(stageBarsSkeleton());
    case "insight-panel":
      return wrap(insightPanelSkeleton());
    case "skill-level-matrix":
      return wrap(skillLevelMatrixSkeleton());
    case "node-cycle":
      return wrap(circularLoopSkeleton());
    case "segmented-cycle":
      return wrap(circularLoopSkeleton());
    case "ecosystem-map":
      return wrap(ecosystemMapSkeleton());
    case "hierarchy-tree":
      return wrap(hierarchyTreeSkeleton());
    case "parallel-category-trees":
      return wrap(parallelCategoryTreesSkeleton());
    case "activity-feed":
      return wrap(activityFeedSkeleton());
    case "metric-list":
      return wrap(metricListSkeleton());
    case "delta-comparison":
      return wrap(deltaComparisonSkeleton());
    case "split-comparison":
      return wrap(splitComparisonSkeleton());
    case "social-proof-stats":
      return wrap(socialProofStatsSkeleton());
    case "list-cards":
      return wrap(listCardsSkeleton());
    case "big-number-chart":
      return wrap(bigNumberChartSkeleton());
    case "pricing-tiers":
      return wrap(pricingTiersSkeleton());
    case "roi-calculator":
      return wrap(roiCalculatorSkeleton());
    case "logo-wall":
      return wrap(logoWallSkeleton());
    case "before-after-annotated":
      return wrap(beforeAfterAnnotatedSkeleton());
    case "before-after-slider":
      return wrap(beforeAfterSliderSkeleton());
    case "dotted-map":
      return wrap(dottedMapSkeleton());
    case "candlestick":
      return wrap(candlestickSkeleton());
    case "funnel":
      return wrap(funnelSkeleton());
    case "heatmap":
      return wrap(withViewToggle(heatmapSkeleton(), view3d));
    case "profit-loss":
      return wrap(profitLossSkeleton());
    case "radar":
      return wrap(radarSkeleton());
    case "sankey":
      return wrap(sankeySkeleton());
    case "sunburst":
      return wrap(sunburstSkeleton());
    case "keywords-grouped":
      return wrap(keywordsGroupedSkeleton());
    case "keywords-explained":
      return wrap(keywordsExplainedSkeleton());
    case "key-takeaways":
      return wrap(keyTakeawaysSkeleton());
    case "faq":
      return wrap(faqSkeleton());
    default:
      return (
        <div className="flex flex-col gap-2">
          <Bar className="h-4 w-1/2" />
          <Bar className="h-3 w-full" />
          <Bar className="h-3 w-5/6" />
        </div>
      );
  }
};
